import { AuthService } from '@hrms/common/service';
import { AppStore, UserStore } from '@hrms/common/store';
import { generateImage } from '@hrms/common/utils/generateImage';
import { Avatar, Dropdown, Typography } from '@zeptonow/zui';
import { useEffect, useState } from 'react';
import { ConfirmationModal } from './common/component/confirmationModal/ConfirmationModal';
import {
  getUserProfileData,
  getUserRoleDetails,
} from './common/store/user/service';
import { useToast } from './common/contexts/toast';
import { initialUserState, ProfileUserStore } from './common/store/user/user';
import { showApiErrorToast } from './common/utils/common';

const LogoutComponent = () => {
  const fullName = ProfileUserStore((state) => state.fullName);
  const contact = ProfileUserStore((state) => state.contact);
  const emailId = ProfileUserStore((state) => state.emailId);
  const roleName = ProfileUserStore((state) => state.roleName);
  const setIsLoggedIn = UserStore((state) => state.setIsLoggedIn);
  const setUserProfileDetails = ProfileUserStore(
    (state) => state.setUserProfile,
  );
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const { openToast } = useToast();

  const openModal = () => setModalVisible(true);
  const closeModal = () => setModalVisible(false);

  const isFormDirty = AppStore((state) => state.isFormDirty);
  const showDropdown = AppStore(
    (state) => state.setShowUnsavedWorkConfirmationModal,
  );

  const onAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isFormDirty) {
      event.stopPropagation();
      showDropdown(true);
    }
  };

  const handleLogout = async () => {
    AuthService.logout();
    closeModal();
    setIsLoggedIn(false);
    setUserProfileDetails(initialUserState);
  };

  const getUserProfilesDetails = async () => {
    try {
      setLoading(true);
      if (!fullName) {
        const response = await getUserProfileData();
        const employeeId = response?.tags?.employeeId?.[0];
        if (employeeId) {
          try {
            const { roleDetails } = await getUserRoleDetails(employeeId);
            const { roleID, isSuperUser } = roleDetails;
            response.roleID = roleID;
            response.isSuperUser = Boolean(isSuperUser);
          } catch (error: any) {
            showApiErrorToast(
              openToast,
              error,
              'Error while fetching user role details',
            );
          }
        }
        setUserProfileDetails(response);
      }
    } catch (error: any) {
      showApiErrorToast(openToast, error, 'Error while fetching user details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUserProfilesDetails();
  }, []);

  const UserLogoutItem = (
    <div className="w-full flex items-center justify-center p-1">
      {generateImage({
        name: 'logout-primary',
        alt: 'logout icon',
        className: 'w-5 h-5',
      })}
      <Typography variant="body2" className="!text-primary-700 !ml-2">
        Logout
      </Typography>
    </div>
  );

  const UserProfileItem = (
    <div className="w-full flex items-center space-x-2">
      <Avatar>
        {generateImage({
          name: 'user',
          alt: 'profile icon',
          className: 'h-5 w-5',
        })}
      </Avatar>
      <Typography className="!text-gray-900 !font-medium !text-base">
        {fullName ?? '-'}
      </Typography>
    </div>
  );

  const UserContactItem = (
    <div className="w-full flex flex-col space-y-2 gap-2">
      <div className="flex items-center space-x-2">
        {generateImage({
          name: 'briefcase-gray',
          alt: 'role icon',
          className: 'h-5 w-5',
        })}
        <Typography className="!text-gray-900 !text-sm !font-medium">
          {roleName ?? '-'}
        </Typography>
      </div>
      <div className="flex items-center space-x-2">
        {generateImage({
          name: 'phone',
          alt: 'phone icon',
          className: 'h-5 w-5',
        })}
        <Typography className="!text-gray-900 !text-sm !font-medium">
          {contact ?? '-'}
        </Typography>
      </div>
      <div className="flex items-center space-x-2 w-full">
        {generateImage({
          name: 'mail',
          alt: 'mail icon',
          className: 'h-5 w-5',
        })}
        <Typography className="!text-gray-900 !text-sm !font-medium text-ellipsis overflow-hidden">
          {emailId ?? '-'}
        </Typography>
      </div>
    </div>
  );

  const Divider = (
    <div
      style={{
        width: '272px',
        height: '1px',
        background:
          'linear-gradient(90deg, rgba(208, 213, 221, 0.00) -0.14%, rgba(208, 213, 221, 0.89) 14.65%, #D0D5DD 85.39%, rgba(208, 213, 221, 0.00) 99.86%)',
      }}
    />
  );

  const dropdownItems = [
    ...(loading
      ? []
      : [
          {
            id: 'profileItem',
            element: UserProfileItem,
          },
          {
            id: 'contactItem',
            element: UserContactItem,
          },
          {
            id: 'divider',
            element: Divider,
          },
        ]),
    {
      id: 'logoutItem',
      element: UserLogoutItem,
      action: openModal,
      ripple: true,
    },
  ];

  return (
    <>
      <Dropdown
        className="!rounded-xl !max-w-xs !max-h-xs !m-0 !p-2"
        items={dropdownItems}
      >
        <div onClickCapture={onAvatarClick}>
          <Avatar>
            {generateImage({
              name: 'user',
              alt: 'profile icon',
              className: 'h-5 w-5',
            })}
          </Avatar>
        </div>
      </Dropdown>

      <ConfirmationModal
        title="Logout"
        description="Are you sure you want to logout?"
        open={modalVisible}
        icon={generateImage({
          name: 'logout-with-bg',
          alt: 'logout icon',
          className: 'h-14 w-14',
        })}
        handleClose={closeModal}
        handleAction={handleLogout}
        actionButtonText="Yes"
      />
    </>
  );
};

export default LogoutComponent;
